import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from './userContext';
import DefaultLoading from './common/DefaultLoading';
import SubscriptionModal from './Onboarding/SubscriptionModal';
import { getAuth, signOut } from 'firebase/auth';

const PrivateRoute = ({ children }) => {
  const { user, loading, subscriptionStatus, refreshUser } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [modalTriggerReason, setModalTriggerReason] = useState('');

  useEffect(() => {
    let modalTimeout;

    const checkSubscriptionStatus = () => {
      // Only show console logs in development
      if (process.env.NODE_ENV === 'development') {
        console.log('🔒 PrivateRoute: Checking subscription status');
        console.log('👤 User:', user ? 'exists' : 'null');
        console.log('⏳ Loading:', loading);
        console.log('📍 Location:', location.pathname);
        console.log('📊 Subscription status:', subscriptionStatus);
      }
      
      if (!loading && user) {
        const hasSessionId = location.search.includes('session_id');
        const isInactive = subscriptionStatus === 'inactive';
        const isExpired = subscriptionStatus === 'expired';
        const isPaymentOverdue = subscriptionStatus === 'payment_overdue';
        const isValidStatus = ['active', 'trialing'].includes(subscriptionStatus);
        
        // Determine if we should show modal and why
        let shouldShowModal = false;
        let reason = '';

        if (hasSessionId) {
          shouldShowModal = true;
          reason = 'subscription_success';
        } else if (isExpired) {
          shouldShowModal = true;
          reason = 'subscription_expired';
        } else if (isPaymentOverdue) {
          shouldShowModal = true;
          reason = 'payment_overdue';
        } else if (isInactive || !isValidStatus) {
          shouldShowModal = true;
          reason = 'no_subscription';
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Modal trigger conditions:');
          console.log('  - Has session_id:', hasSessionId);
          console.log('  - Is expired:', isExpired);
          console.log('  - Is payment overdue:', isPaymentOverdue);
          console.log('  - Is inactive:', isInactive);
          console.log('  - Has valid status:', isValidStatus);
          console.log('  - Should show modal:', shouldShowModal, '(Reason:', reason + ')');
        }
        
        if (shouldShowModal) {
          setModalTriggerReason(reason);
          modalTimeout = setTimeout(() => {
            setShowModal(true);
            if (process.env.NODE_ENV === 'development') {
              console.log('🎭 Modal is now showing for reason:', reason);
            }
          }, 500);
        } else {
          setShowModal(false);
          setModalTriggerReason('');
        }
        setIsReady(true);
      } else if (!loading && !user) {
        setIsReady(true);
      }
    };

    checkSubscriptionStatus();

    return () => {
      if (modalTimeout) {
        clearTimeout(modalTimeout);
      }
    };
  }, [user, subscriptionStatus, location.search, location.pathname, loading]);

  const handleSignOut = () => {
    const auth = getAuth();
    signOut(auth)
      .then(() => {
        // Clear any cached data
        localStorage.removeItem('Auth Token');
        sessionStorage.clear();
        navigate('/studio/login');
      })
      .catch((error) => {
        // Handle sign out error silently in production
        if (process.env.NODE_ENV === 'development') {
          console.error('Sign out error:', error);
        }
        // Force navigation even if signout fails
        navigate('/studio/login');
      });
  };

  const handleModalClose = () => {
    setShowModal(false);
    setModalTriggerReason('');
    
    // Only redirect if user doesn't have valid subscription
    const hasValidSubscription = ['active', 'trialing', 'payment_overdue'].includes(subscriptionStatus);
    if (!hasValidSubscription && modalTriggerReason !== 'subscription_success') {
      navigate('/studio');
    }
  };

  // Show loading while checking authentication and subscription
  if (loading || !isReady) {
    return <DefaultLoading />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/studio/login" replace state={{ from: location }} />;
  }

  // Allow access to dashboard even with subscription issues, modal will handle the rest
  return (
    <>
      {children}

      <SubscriptionModal
        showModal={showModal}
        setShowModal={setShowModal}
        handleSignOut={handleSignOut}
        onClose={handleModalClose}
        triggerReason={modalTriggerReason}
      />
    </>
  );
};

export default PrivateRoute;
